/* eslint arrow-body-style: 0 */
import { frontendURL } from '../../../helper/URLHelper';
import { isFeatureFlagEnabledForAccount } from '../../../helper/featureFlagHelper';
import { REDIS_FEATURE_FLAGS } from '../../../featureFlags';
import store from '../../../store';
const ConversationView = () => import('./ConversationView.vue');

const CONVERSATION_PERMISSIONS = [
  'administrator',
  'agent',
  'conversation_manage',
  'conversation_unassigned_manage',
  'conversation_participating_manage',
];

// The audit view only makes sense for who can actually audit: the audit footer
// is rendered for administrators and CX engineers only (see `showAuditFooter`
// in MessagesView.vue). Keep both gates in sync — a supervisor reaching this
// list would open conversations with no audit form. The sidebar item inherits
// this list through `routesWithPermissions` (see Secondary.vue).
const AUDIT_PERMISSIONS = ['administrator', 'cx_engineer'];

// The sidebar already hides the parallel entry behind the flag, but the URL is
// reachable on its own — bookmarked, pasted, or left over from when the flag was
// on. Scoped by the TARGET account, like the Improvement Center guard, because
// the route can cross accounts. Sends the user to the conversation dashboard
// rather than a 404: with the feature off, that list is simply not theirs.
const guardParallelConversationsFlag = (to, from, next) => {
  const { accountId } = to.params;
  const account = (store.getters.getCurrentUser?.accounts || []).find(
    item => Number(item.id) === Number(accountId)
  );

  if (
    isFeatureFlagEnabledForAccount(
      account,
      REDIS_FEATURE_FLAGS.PARALLEL_CONVERSATIONS
    )
  ) {
    next();
    return;
  }

  next(frontendURL(`accounts/${accountId}/dashboard`));
};

export default {
  routes: [
    {
      path: frontendURL('accounts/:accountId/dashboard'),
      name: 'home',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: () => {
        return { inboxId: 0 };
      },
    },
    {
      path: frontendURL('accounts/:accountId/conversations/:conversation_id'),
      name: 'inbox_conversation',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => {
        return { inboxId: 0, conversationId: route.params.conversation_id };
      },
    },
    {
      path: frontendURL('accounts/:accountId/inbox/:inbox_id'),
      name: 'inbox_dashboard',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => {
        return { inboxId: route.params.inbox_id };
      },
    },
    {
      path: frontendURL(
        'accounts/:accountId/inbox/:inbox_id/conversations/:conversation_id'
      ),
      name: 'conversation_through_inbox',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => {
        return {
          conversationId: route.params.conversation_id,
          inboxId: route.params.inbox_id,
        };
      },
    },
    {
      path: frontendURL('accounts/:accountId/label/:label'),
      name: 'label_conversations',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => ({ label: route.params.label }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/label/:label/conversations/:conversation_id'
      ),
      name: 'conversations_through_label',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversation_id,
        label: route.params.label,
      }),
    },
    {
      path: frontendURL('accounts/:accountId/team/:teamId'),
      name: 'team_conversations',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => ({ teamId: route.params.teamId }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/team/:teamId/conversations/:conversationId'
      ),
      name: 'conversations_through_team',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversationId,
        teamId: route.params.teamId,
      }),
    },
    {
      path: frontendURL('accounts/:accountId/custom_view/:id'),
      name: 'folder_conversations',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => ({ foldersId: route.params.id }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/custom_view/:id/conversations/:conversation_id'
      ),
      name: 'conversations_through_folders',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversation_id,
        foldersId: route.params.id,
      }),
    },
    {
      path: frontendURL('accounts/:accountId/mentions/conversations'),
      name: 'conversation_mentions',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: () => ({ conversationType: 'mention' }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/mentions/conversations/:conversationId'
      ),
      name: 'conversation_through_mentions',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversationId,
        conversationType: 'mention',
      }),
    },
    {
      path: frontendURL('accounts/:accountId/unattended/conversations'),
      name: 'conversation_unattended',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: () => ({ conversationType: 'unattended' }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/unattended/conversations/:conversationId'
      ),
      name: 'conversation_through_unattended',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversationId,
        conversationType: 'unattended',
      }),
    },
    {
      path: frontendURL('accounts/:accountId/parallel/conversations'),
      name: 'conversation_parallel',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      beforeEnter: guardParallelConversationsFlag,
      component: ConversationView,
      props: () => ({ conversationType: 'parallel_requested' }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/parallel/conversations/:conversationId'
      ),
      name: 'conversation_through_parallel',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      beforeEnter: guardParallelConversationsFlag,
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversationId,
        conversationType: 'parallel_requested',
      }),
    },
    {
      // ClaudIA audit view: the same conversation list, opened with the audit
      // preset filters (resolved + last 7 days + ClaudIA inboxes) already
      // applied by ChatList. It is a list scope only — opening a conversation
      // from here falls back to the regular conversation route, carrying the
      // filters in the query params like any other filtered list.
      path: frontendURL('accounts/:accountId/audit/conversations'),
      name: 'conversation_audit',
      meta: {
        permissions: AUDIT_PERMISSIONS,
      },
      component: ConversationView,
      props: () => ({ inboxId: 0, isAuditView: true }),
    },
    {
      path: frontendURL('accounts/:accountId/participating/conversations'),
      name: 'conversation_participating',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: () => ({ conversationType: 'participating' }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/participating/conversations/:conversationId'
      ),
      name: 'conversation_through_participating',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversationId,
        conversationType: 'participating',
      }),
    },
  ],
};
